import { BaseDragger } from "./BaseDragger";
import type { DraggerManager } from "./DraggerManager";
import {
  DraggerType,
  type Point2D,
  type Point3D,
  type DraggerConfig,
  type TypedInputMode,
  type TypedInputHint,
  type TypedInputSpec,
} from "./types";
import type { DrawingModule } from "../types/DrawingWeb";
import type { DRDrawingViewer } from "../DRDrawingViewer";
import { deleteObj } from "../core/delete";
import { openAs } from "../core/oda";
import { getEntityHandle } from "../core/oda";
import { createViewGhost } from "../entities/ghost";
import { selectionService } from "../selection";
import { TransformEntityCommand } from "../commands/TransformEntityCommand";
import {
  IDENTITY_TRANSFORM,
  applyTransformDelta,
  applyTransformToEntityIds,
  isIdentityTransform,
  snapshotTransform,
  type EntityTransform,
  type EntityTransformParams,
} from "../transform/entityTransform";
import { PREVIEW_RUBBER_BAND_STYLE } from "./previewStyles";
import { isShortcutBlockingTarget } from "@/utils/keyboardGuards";
import { Logger } from "@/logger";

/**
 * Alpha applied to an original while its ghost stands in for it. 0 = fully
 * transparent, so the original is effectively hidden without leaving the view
 * or the database. Same 0..1 scale BaseHandle.setEntityTransparency() uses.
 */
const HIDDEN_ORIGINAL_ALPHA = 0;

/**
 * Abstract base for AutoCAD-style "pick a base point, then pick more points"
 * edit commands operating on the current selection — COPY, MOVE, ROTATE, SCALE.
 *
 * All four share the same skeleton and differ only in three things, which is
 * exactly what subclasses supply:
 *  - how many points they need after the base point (`followUpPointCount`),
 *  - what transform those points imply (`computeTransform`),
 *  - what to do with it (`commit`) — clone, or transform in place.
 *
 * Device ergonomics fall out of the existing gesture/hover pipeline without any
 * pointerType branching:
 * - Mouse: a hover event (BaseDragger.handleHoverMove, routed by
 *   InteractionManager) only ever fires for a real mouse with no button held.
 *   Once the base point is fixed getSnapReferencePoint() becomes non-null, so
 *   the ghost keeps following the cursor between clicks — classic AutoCAD feel.
 * - Touch/pen: no hover event is ever generated without contact, so the ghost
 *   only moves during a real press-drag gesture (onDrag) and stays frozen at
 *   the last confirmed position between gestures — the discrete tap-drag-release
 *   model tablets need.
 */
export abstract class BasePointDragger extends BaseDragger {
  protected manager: DraggerManager;

  /**
   * Captured once on activation and never re-read, so every commit transforms
   * or offsets the ORIGINAL entities — never a previously placed copy.
   */
  protected originalEntityIds: DrawingModule.OdDbObjectId[] = [];

  /** Point 0. Null until the first release fixes it. */
  protected basePoint: Point3D | null = null;

  /** Points 1..n-1, already fixed. */
  protected followUpPoints: Point3D[] = [];

  private ghosts: DrawingModule.OdDbEntity[] = [];

  /** The transform the ghosts currently reflect. */
  private appliedTransform: EntityTransform = { ...IDENTITY_TRANSFORM };

  /**
   * The originals whose transparency was overridden to hide them behind their
   * ghosts, each with its prior transparency captured (serialized) so teardown
   * restores exactly what was there before. Tracked individually rather than
   * with a flag: if createViewGhost() fails for some entities, only the ones
   * that got a ghost were dimmed, and the rest must be left untouched.
   */
  private dimmedOriginals: Array<{
    id: DrawingModule.OdDbObjectId;
    transparency: number;
  }> = [];

  private guideLineId: string | null = null;

  /**
   * World point the preview currently runs to. Kept as a world point (not a
   * screen point) so the guide line can be re-projected after a pan/zoom, when
   * no pointer event will come along to refresh it.
   */
  protected lastPreviewWorld: Point3D | null = null;

  private keyDownHandler: ((ev: KeyboardEvent) => void) | null = null;
  private unsubscribeViewChanged: (() => void) | null = null;
  private pendingChangeHandlers: Array<(pending: boolean) => void> = [];
  private promptChangeHandlers: Array<(prompt: string | null) => void> = [];

  // The tool active right before this one took over (e.g. RectangleDragger,
  // which stays the active tool — ready to draw the next shape — even while
  // its just-created shape is auto-selected). Restored on finish() instead of
  // hardcoding Select, so invoking an edit tool from a selection made by
  // auto-select doesn't strand the user out of the tool they were using.
  private previousDraggerType: DraggerType = DraggerType.Select;

  constructor(
    viewer: DRDrawingViewer,
    manager: DraggerManager,
    config: DraggerConfig = {},
  ) {
    super(viewer, config);
    this.manager = manager;
  }

  // ============== Subclass contract ==============

  /** Points required AFTER the base point before committing. */
  protected abstract readonly followUpPointCount: number;

  /** Stay active and commit again from the same base point (COPY does). */
  protected readonly repeats: boolean = false;

  /**
   * Hide the originals behind the ghosts while previewing. False for COPY (the
   * originals must stay visible — you are copying them), true for the tools
   * that move the originals themselves.
   */
  protected readonly hidesOriginals: boolean = true;

  /**
   * The transform implied by the base point, the follow-up points fixed so far,
   * and the live cursor position. Return an identity transform for steps that
   * have nothing to preview yet (e.g. SCALE before its reference point).
   */
  protected abstract computeTransform(current: Point3D): EntityTransform;

  /** Persist the operation. */
  protected abstract commit(transform: EntityTransform): void;

  /** i18n key for the current step's on-canvas prompt. */
  protected abstract promptKey(): string;

  /**
   * The modes this tool accepts as typed input at the current step, or null
   * when the step can only be satisfied by a pick (every tool's base point) or
   * the tool has no typed entry at all. Null by default.
   */
  protected typedInputModes(): TypedInputMode[] | null {
    return null;
  }

  /** i18n key for the keypad's header. Follows the step by default. */
  protected typedInputTitleKey(): string {
    return this.promptKey();
  }

  /** Read-only figure to show alongside the fields, if the step has one. */
  protected typedInputHint(): TypedInputHint | null {
    return null;
  }

  /**
   * Live values for typedInputFields(), read off the current preview point so
   * the keypad mirrors the drag. `current` is null before anything has been
   * previewed.
   */
  protected typedInputValues(_current: Point3D | null): Record<string, number> {
    return {};
  }

  /**
   * The world point the typed values stand for — fed to the very same
   * previewAt()/acceptPoint() path a pointer would take, so typed entry and
   * picking share one implementation of every step. `modeId` says how to read
   * them. Null when the values are incomplete or degenerate.
   */
  protected worldPointFromTypedValues(
    _modeId: string,
    _values: Record<string, number>
  ): Point3D | null {
    return null;
  }

  // ============== Lifecycle ==============

  protected onActivate(): void {
    this.originalEntityIds = selectionService.getAll();

    // Deselect immediately: this tears down the selection's transform grid
    // (grips) right away, same as every other tool activation normally does via
    // DraggerMenuState.setActiveDragger() -> Facade.selection.clear(). Without
    // this the grips would stay visible and hit-testable on top of the
    // originals for the whole operation, competing with this dragger's own
    // clicks for the base/follow-up points.
    selectionService.clear();
    this.viewer.update();

    // DraggerManager.activate() deactivates the previous dragger before calling
    // this one's activate()/onActivate(), but only reassigns its own `current`
    // reference *after* activate() returns — so manager.currentType here still
    // reports the dragger that was active right before this one.
    const prevType = this.manager.currentType;
    this.previousDraggerType =
      prevType === DraggerType.None || prevType === this.type
        ? DraggerType.Select
        : prevType;

    this.keyDownHandler = (ev: KeyboardEvent) => {
      if (isShortcutBlockingTarget(ev.target, ev.key)) return;
      if (ev.key === "Escape") {
        this.finish();
      }
    };
    window.addEventListener("keydown", this.keyDownHandler);

    // Re-anchor the guide line after a pan/zoom: between two clicks there is no
    // pointer event to redraw it, so without this it would stay pinned to stale
    // screen coordinates.
    this.unsubscribeViewChanged = this.viewer.onViewChanged(() => {
      if (!this.basePoint) return;
      // Wait a frame so the new view transform is applied before reprojecting.
      requestAnimationFrame(() => this.redrawGuideLine());
    });

    this.emitPendingPlacementChange(true);
    this.emitPromptChange();
    this.emitTypedInputChange();
  }

  protected onDeactivate(): void {
    if (this.keyDownHandler) {
      window.removeEventListener("keydown", this.keyDownHandler);
      this.keyDownHandler = null;
    }
    if (this.unsubscribeViewChanged) {
      this.unsubscribeViewChanged();
      this.unsubscribeViewChanged = null;
    }

    // Restoring the originals to the view MUST happen here rather than only in
    // finish(): this is the one hook guaranteed to run however the tool stops
    // being active, including DraggerManager silently swapping in another tool
    // when the user taps a different button mid-operation. Skipping it there
    // would leave the originals invisible.
    this.destroyGhosts();
    this.clearGuideLine();

    this.basePoint = null;
    this.followUpPoints = [];
    this.lastPreviewWorld = null;
    this.appliedTransform = { ...IDENTITY_TRANSFORM };
    this.originalEntityIds = [];

    this.emitPendingPlacementChange(false);
    this.emitPromptChange();
    this.emitTypedInputChange();
  }

  // ============== Dragger callbacks ==============

  protected onStart(screen: Point2D, world: Point3D): void {
    // The base point is always taken from where a gesture *ends*, not starts —
    // but if it is already fixed, show a live preview immediately in case this
    // gesture turns out to be a real drag.
    if (this.basePoint) {
      this.previewAt(world);
    }
  }

  protected onDrag(screen: Point2D, world: Point3D): void {
    if (!this.basePoint) return;
    this.previewAt(world);
  }

  protected onEnd(screen: Point2D, world: Point3D): void {
    this.acceptPoint(world);
  }

  /**
   * Advance the tool by one point. The single entry point for both a released
   * gesture and a validated keypad entry, so typed input goes through exactly
   * the same state machine — base point, then intermediate points, then commit.
   */
  protected acceptPoint(world: Point3D): void {
    if (!this.basePoint) {
      this.basePoint = world;
      this.createGhosts();
      this.viewer.update();
      this.emitPromptChange();
      this.emitTypedInputChange();
      return;
    }

    // Still collecting intermediate points (only SCALE has one).
    if (this.followUpPoints.length < this.followUpPointCount - 1) {
      if (!this.acceptFollowUpPoint(world)) return;
      this.followUpPoints.push(world);
      this.previewAt(world);
      this.emitPromptChange();
      this.emitTypedInputChange();
      return;
    }

    // Final point: commit.
    // Deliberately no "degenerate click" guard: unlike shape creation, landing
    // on the base point is legitimate here (stacking a copy, a zero-length
    // move) rather than an accidental zero-size shape.
    const transform = this.computeTransform(world);
    this.lastPreviewWorld = world;
    this.moveGhostsTo(transform);
    this.redrawGuideLine();
    this.commit(transform);
    this.viewer.update();

    if (this.repeats) {
      this.emitPromptChange();
      this.emitTypedInputChange();
      return;
    }
    this.finish();
  }

  /**
   * Validate an intermediate point before it is fixed. Returning false rejects
   * the pick and leaves the step unchanged — used by SCALE to refuse a
   * zero-length reference that would make the factor infinite.
   */
  protected acceptFollowUpPoint(_world: Point3D): boolean {
    return true;
  }

  /**
   * Pure mouse hover (no button held) once the base point is fixed — keeps the
   * ghost following the cursor between clicks. Never fires for touch/pen.
   */
  override handleHoverMove(screen: Point2D, world: Point3D): void {
    if (!this.basePoint) return;
    this.previewAt(world);
  }

  private previewAt(world: Point3D): void {
    if (!this.basePoint) return;
    this.lastPreviewWorld = world;
    this.moveGhostsTo(this.computeTransform(world));
    this.redrawGuideLine();
    this.viewer.update();
    this.emitTypedInputChange();
  }

  override getSnapReferencePoint(): Point3D | null {
    return this.basePoint;
  }

  // Points are fixed on release: where the drag starts is not one of them.
  override snapsToGestureStart(): boolean {
    return false;
  }

  // ============== Undo/redo safety ==============

  /**
   * Undo/redo while this tool is active can reach past its own commits into
   * whatever command created `originalEntityIds`' entities in the first place —
   * soft-deleting (hidden layer + invisible) the very entities this dragger
   * still holds references to and would go on to clone or transform, neither of
   * which guards against that. Rather than trying to replay/resync internal
   * state like SequentialDragger does, simply end the tool first (keeping
   * anything already committed) and then let the real undo/redo proceed —
   * returning false means we are not intercepting it, just reacting before
   * CommandHistory runs.
   */
  override handleUndo(): boolean {
    this.finish();
    return false;
  }

  override handleRedo(): boolean {
    this.finish();
    return false;
  }

  // ============== Pending placement / prompt (on-canvas affordances) ==============

  // True for as long as the tool is active. Unlike TwoPointDragger there is no
  // meaningful "cancel just the base point but keep the tool active" state —
  // AutoCAD's COPY/MOVE have no such affordance either, you simply end the
  // command. So this drives a single button for the whole tool lifetime rather
  // than a per-point cancel.
  override hasPendingPlacement(): boolean {
    return this.isActive;
  }

  override onPendingPlacementChange(
    handler: (pending: boolean) => void,
  ): () => void {
    this.pendingChangeHandlers.push(handler);
    return () => {
      const index = this.pendingChangeHandlers.indexOf(handler);
      if (index >= 0) this.pendingChangeHandlers.splice(index, 1);
    };
  }

  override getStepPrompt(): string | null {
    return this.isActive ? this.promptKey() : null;
  }

  override onStepPromptChange(
    handler: (prompt: string | null) => void,
  ): () => void {
    this.promptChangeHandlers.push(handler);
    return () => {
      const index = this.promptChangeHandlers.indexOf(handler);
      if (index >= 0) this.promptChangeHandlers.splice(index, 1);
    };
  }

  // ============== Typed numeric input ==============

  override getTypedInputSpec(): TypedInputSpec | null {
    if (!this.isActive) return null;
    const modes = this.typedInputModes();
    if (!modes || modes.length === 0) return null;
    return {
      titleKey: this.typedInputTitleKey(),
      modes,
      values: this.typedInputValues(this.lastPreviewWorld),
      hint: this.typedInputHint() ?? undefined,
    };
  }

  override previewTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    const world = this.worldPointFromTypedValues(modeId, values);
    if (world) this.previewAt(world);
  }

  override commitTypedInput(
    modeId: string,
    values: Record<string, number>
  ): void {
    const world = this.worldPointFromTypedValues(modeId, values);
    if (world) this.acceptPoint(world);
  }

  /**
   * Ends the tool. Anything already committed stays (each commit is an
   * independent, undo-registered change — nothing to discard); anything not yet
   * committed is simply dropped, since the originals are untouched until
   * commit(). Selection is cleared, matching AutoCAD. Switches back to whichever
   * tool was active before. Called by the on-canvas button and by Escape.
   */
  override cancelPendingPlacement(): void {
    this.finish();
  }

  protected finish(): void {
    if (!this.isActive) return;

    selectionService.clear();
    // Covers the case where nothing has been placed yet (destroyGhosts() is
    // then a no-op and wouldn't otherwise trigger a render) — ghost/guide-line
    // teardown and the pending-placement notification happen in onDeactivate(),
    // triggered by manager.activate() just below.
    this.viewer.update();

    this.manager.activate(this.previousDraggerType);
  }

  private emitPendingPlacementChange(pending: boolean): void {
    for (const handler of this.pendingChangeHandlers) handler(pending);
  }

  private emitPromptChange(): void {
    const prompt = this.getStepPrompt();
    for (const handler of this.promptChangeHandlers) handler(prompt);
  }


  // ============== Commit helper for in-place transforms ==============

  /**
   * Apply a transform to the original entities and register it for undo.
   *
   * Mirrors the contract BaseHandle.end() relies on: the transform is applied
   * here, and TransformEntityCommand exists to replay the inverse on undo (its
   * execute() is a no-op the first time round). Hatches follow automatically —
   * the command syncs every transformed boundary.
   */
  protected commitTransformInPlace(transform: EntityTransform): void {
    if (isIdentityTransform(transform)) return;

    const ctx = selectionService.getSelectionSet().getContext();
    if (!ctx?.history) return;

    // Snapshotted: the caller recomputes its transform every frame, and these
    // params outlive the drag on the undo stack.
    const applied = snapshotTransform(transform);
    const params: EntityTransformParams[] = this.originalEntityIds.map(
      (id) => ({
        handle: getEntityHandle(id),
        transform: applied,
      }),
    );

    applyTransformToEntityIds(this.module, this.originalEntityIds, transform);

    ctx.history.execute(new TransformEntityCommand(this.viewer, params));
  }

  // ============== Ghost rendering ==============

  private createGhosts(): void {
    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);
    if (!view) return;

    for (const entityId of this.originalEntityIds) {
      const ghost = createViewGhost(this.module, this.viewer, entityId);
      if (!ghost) continue;

      this.ghosts.push(ghost);
      view.add(ghost, null as unknown as DrawingModule.OdGsModel);

      if (this.hidesOriginals) {
        // The ghost stands in for the original while dragging. Rather than
        // erasing the original from the view (which risks stranding it as gone
        // if teardown is skipped), just make it transparent — the AutoCAD
        // approach. It stays in the view and the database the whole time.
        this.hideOriginal(entityId);
      }
    }
    device?.invalidate();
  }

  /**
   * Override the original's transparency to hide it behind its ghost, capturing
   * its prior transparency for restoreOriginal(). Undo recording is suppressed
   * around the change so it never lands between the tool's real commits, and
   * teardown restores it regardless.
   */
  private hideOriginal(entityId: DrawingModule.OdDbObjectId): void {
    const original = openAs(
      entityId,
      this.module.OdDbEntity,
      1 /* kForWrite */,
    );
    try {
      original.disableUndoRecording(true);
      const current = original.transparency();
      this.dimmedOriginals.push({
        id: entityId,
        transparency: current.serializeOut(),
      });
      deleteObj(current);

      const hidden = new this.module.OdCmTransparency();
      hidden.setAlpha(HIDDEN_ORIGINAL_ALPHA);
      original.setTransparency(hidden, true);
      original.recordGraphicsModified(true);
      deleteObj(hidden);
    } finally {
      original.disableUndoRecording(false);
      deleteObj(original);
    }
  }

  /** Restore an original's transparency to what it was before hideOriginal(). */
  private restoreOriginal(entry: {
    id: DrawingModule.OdDbObjectId;
    transparency: number;
  }): void {
    const original = openAs(
      entry.id,
      this.module.OdDbEntity,
      1 /* kForWrite */,
    );
    try {
      original.disableUndoRecording(true);
      const restored = new this.module.OdCmTransparency();
      restored.serializeIn(entry.transparency);
      original.setTransparency(restored, true);
      original.recordGraphicsModified(true);
      deleteObj(restored);
    } finally {
      original.disableUndoRecording(false);
      deleteObj(original);
    }
  }

  /**
   * Show a transform on the ghosts that no point stands for — SCALE previewing
   * a bare typed factor, which has no reference distance yet and therefore no
   * cursor position to rubber-band to. The guide line is deliberately left
   * where it is rather than cleared: it still marks the base point.
   */
  protected previewTransform(next: EntityTransform): void {
    this.moveGhostsTo(next);
    this.viewer.update();
  }

  private moveGhostsTo(next: EntityTransform): void {
    applyTransformDelta(this.module, this.ghosts, this.appliedTransform, next);
    this.appliedTransform = next;
  }

  private destroyGhosts(): void {
    // Restore transparency first, even when there are no ghosts to tear down:
    // this is the one path guaranteed to run however the tool stops, so a
    // dimmed original must never be left behind.
    const originalsToRestore = this.dimmedOriginals;
    this.dimmedOriginals = [];
    for (const entry of originalsToRestore) {
      try {
        this.restoreOriginal(entry);
      } catch (e) {
        // A single unreadable entity must not strand the others as
        // invisible, nor abort the ghost teardown below.
        Logger.captureException(e, {
          source: `${this.constructor.name}.destroyGhosts.restoreOriginal`,
        });
      }
    }

    if (this.ghosts.length === 0) return;

    const device = this.viewer.app?.getDevice();
    const view = device?.viewAt(0);
    const ghostsToDelete = this.ghosts;
    this.ghosts = [];

    if (view) {
      for (const ghost of ghostsToDelete) {
        view.erase(ghost);
      }
    }
    device?.invalidate();

    try {
      this.viewer.update();
    } catch (e) {
      // Logged, not rethrown: finish()/onDeactivate() must still complete
      // (switch back to the previous tool, restore selection) even if this
      // render update hiccups.
      Logger.captureException(e, {
        source: `${this.constructor.name}.destroyGhosts`,
      });
    } finally {
      // Safe to free ghost memory only now: view.erase() only queues the
      // removal, ODA's render pipeline still holds the pointer until the update
      // above completes — freeing earlier risks a WASM use-after-free
      // ("null function") crash. Same pattern as BaseHandle.
      for (const ghost of ghostsToDelete) {
        deleteObj(ghost);
      }
    }
  }

  // ============== Guide line overlay ==============

  /**
   * Draw the rubber band from the base point to the live preview point. Both
   * ends are projected from world coordinates on every call, so this doubles as
   * the reprojection path after a pan/zoom.
   */
  private redrawGuideLine(): void {
    if (!this.basePoint || !this.lastPreviewWorld) return;
    const overlay = this.viewer.overlayManager;
    if (!overlay) return;

    const from = this.viewer.worldToScreen(this.basePoint);
    const to = this.viewer.worldToScreen(this.lastPreviewWorld);

    if (this.guideLineId) {
      overlay.update(this.guideLineId, {
        x1: from.x,
        y1: from.y,
        x2: to.x,
        y2: to.y,
      });
    } else {
      this.guideLineId = overlay.createLine(
        from.x,
        from.y,
        to.x,
        to.y,
        PREVIEW_RUBBER_BAND_STYLE,
      );
    }
  }

  private clearGuideLine(): void {
    if (!this.guideLineId) return;
    this.viewer.overlayManager?.remove(this.guideLineId);
    this.guideLineId = null;
  }
}
